import React from 'react';

import { Alert, Checkbox, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

import {
  BulkPatchSupplierPropertyField,
  BulkPatchSupplierPropertyPreviewRow,
  BulkPatchSupplierPropertyValueChange,
} from '~/services/BedbankService';

import { isSelectableAction } from './BulkSupplierPropertyUpdateUtils';

const PATCH_FIELDS: Array<BulkPatchSupplierPropertyField> = [
  'supplierPropertyId',
  'travelgateId',
  'supplierEnabled',
  'metasearchEnabled',
];

function getRowKey(row: Pick<BulkPatchSupplierPropertyPreviewRow, 'propertyId' | 'supplier'>) {
  return `${row.propertyId}:${row.supplier}`;
}

function renderPreviewValue(
  change: BulkPatchSupplierPropertyValueChange | undefined,
  action: BulkPatchSupplierPropertyPreviewRow['action'],
) {
  if (!change) {
    return '-';
  }

  const oldValue = typeof change.oldValue === 'undefined' ? '' : (change.oldValue ?? 'null');
  const newValue = typeof change.newValue === 'undefined' ? '' : (change.newValue ?? 'null');
  if (action === 'create') {
    return `${newValue}`;
  }

  return oldValue === newValue ? `${oldValue}` : `${oldValue} ⇒ ${newValue}`;
}

interface Props {
  rows: Array<BulkPatchSupplierPropertyPreviewRow>;
  selectedRowKeys: Set<string>;
  onToggleRow: (row: BulkPatchSupplierPropertyPreviewRow) => void;
  onToggleAllRows: () => void;
}

export default function BulkSupplierPropertyUpdatePreviewTable({
  rows,
  selectedRowKeys,
  onToggleRow,
  onToggleAllRows,
}: Props) {
  const selectableRows = rows.filter((row) => isSelectableAction(row.action));

  if (rows.length === 0) {
    return <Alert severity="info">Run preview to see changes.</Alert>;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              checked={selectableRows.length > 0 && selectedRowKeys.size === selectableRows.length}
              indeterminate={selectedRowKeys.size > 0 && selectedRowKeys.size < selectableRows.length}
              onChange={onToggleAllRows}
            />
          </TableCell>
          <TableCell>Property ID</TableCell>
          <TableCell>Supplier</TableCell>
          <TableCell>Action</TableCell>
          <TableCell>Content Pull</TableCell>
          <TableCell>Supplier Property ID</TableCell>
          <TableCell>Travelgate ID</TableCell>
          <TableCell>Supplier Enabled</TableCell>
          <TableCell>Metasearch Enabled</TableCell>
          <TableCell>Error</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => {
          const isSelectable = isSelectableAction(row.action);
          return (
            <TableRow key={getRowKey(row)} selected={selectedRowKeys.has(getRowKey(row))}>
              <TableCell padding="checkbox">
                <Checkbox
                  disabled={!isSelectable}
                  checked={selectedRowKeys.has(getRowKey(row))}
                  onChange={() => onToggleRow(row)}
                />
              </TableCell>
              <TableCell>{row.propertyId}</TableCell>
              <TableCell>{row.supplier}</TableCell>
              <TableCell>{row.action}</TableCell>
              <TableCell>
                {typeof row.contentPullRequired === 'undefined' ? '-' : `${row.contentPullRequired}`}
              </TableCell>
              {PATCH_FIELDS.map((field) => (
                <TableCell key={field}>{renderPreviewValue(row[field], row.action)}</TableCell>
              ))}
              <TableCell>{row.error ?? '-'}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
